import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import LanguageSelector from '../components/LanguageSelector';
import './Auth.css';

const Register = () => {
  const { t, i18n } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    phone: '',
    password: '',
    display_name: '',
    language: i18n.language,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  // Sync form language with i18n language
  useEffect(() => {
    setFormData(prev => ({ ...prev, language: i18n.language }));
  }, [i18n.language]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await register(formData);
      navigate('/');
    } catch (err) {
      setError(err.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-split fade-in">
        {/* Left side - Branding */}
        <div className="auth-brand">
          <div className="brand-curves"></div>
          <div className="brand-content">
            <div className="brand-logo">
              <img src="/header-icon.png" alt="emoozi" />
              <span className="brand-logo-text">emoozi</span>
            </div>
            <h1 className="brand-headline">
              Your people. Your space.
            </h1>
            <p className="brand-subheadline">
              {t('auth.brandSubheadline')}
            </p>
          </div>
        </div>

        {/* Right side - Form */}
        <div className="auth-form-side">
          <div className="auth-card">
            <div className="auth-card-header">
              <h2>{t('auth.createAccount')}</h2>
              <p className="auth-card-subtitle">{t('auth.startPrivateSpace')}</p>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="form-group">
                <label>{t('auth.username')}</label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder={t('auth.chooseUsername')}
                  required
                  minLength={3}
                />
              </div>

              <div className="form-group">
                <label>{t('auth.email')}</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('auth.enterYourEmail')}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('auth.displayName')}</label>
                <input
                  type="text"
                  name="display_name"
                  value={formData.display_name}
                  onChange={handleChange}
                  placeholder={t('auth.yourDisplayName')}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('auth.phone')}</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder={t('auth.yourPhoneNumber')}
                />
              </div>

              <div className="form-group">
                <label>{t('auth.password')}</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={t('auth.createPassword')}
                  required
                  minLength={6}
                />
              </div>

              <p className="terms-notice">
                {t('auth.byClickingContinue')}{' '}
                <button type="button" className="terms-link" onClick={() => setShowTerms(true)}>
                  {t('auth.termsOfUse')}
                </button>{' '}
                và{' '}
                <button type="button" className="terms-link" onClick={() => setShowTerms(true)}>
                  {t('auth.privacyPolicy')}
                </button>
              </p>

              <button type="submit" className="primary" disabled={loading}>
                {loading ? <span className="loading-spinner-small"></span> : t('auth.continue')}
              </button>
            </form>

            <p className="trust-signal">
              {t('auth.privateByDefault')}
            </p>

            <p className="auth-footer">
              {t('auth.alreadyHaveAccount')}{' '}
              <Link to="/login">{t('auth.login')}</Link>
            </p>

            <LanguageSelector />
          </div>
        </div>
      </div>

      {/* Terms & Privacy Modal - Full Content from MD files */}
      {showTerms && (
        <div className="modal-overlay" onClick={() => setShowTerms(false)}>
          <div className="modal-content terms-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowTerms(false)}>×</button>
            <div className="terms-content">
              {/* ===== TERMS OF USE - ENGLISH ===== */}
              <h2>Terms of Use</h2>
              <p><em>Effective Date: March 4, 2026</em></p>

              <h3>1. Acceptance of Terms</h3>
              <p>By accessing and using emoozi ("the Service"), you agree to be bound by these Terms of Use. If you do not agree to these terms, please do not use our Service.</p>

              <h3>2. Description of Service</h3>
              <p>emoozi is a private social network platform that allows users to share moments, chat with family and friends, and stay connected in a private, algorithm-free environment.</p>

              <h3>3. Legal Basis</h3>
              <p>This Terms of Use is prepared in compliance with:</p>
              <ul>
                <li>Vietnam Cybersecurity Law No. 24/2018/QH14</li>
                <li>Civil Code of Vietnam</li>
                <li>Law on Information Security</li>
                <li>Law on Protection of Consumer Rights</li>
              </ul>

              <h3>4. User Registration and Account</h3>
              <p>4.1. You must provide accurate, complete, and up-to-date registration information.</p>
              <p>4.2. You are responsible for maintaining the confidentiality of your account credentials.</p>
              <p>4.3. You must be at least 13 years of age to use this Service.</p>
              <p>4.4. You are solely responsible for all activities under your account.</p>
              <p>4.5. You must provide valid identification when required by law.</p>

              <h3>5. User Obligations (Per Article 5 of Cybersecurity Law)</h3>
              <p>You agree to:</p>
              <ul>
                <li>Comply with Vietnamese laws and these Terms of Use</li>
                <li>Provide accurate and complete personal information</li>
                <li>Not use the Service for unlawful purposes</li>
                <li>Not infringe upon the rights and legitimate interests of others</li>
                <li>Promptly update your information when changes occur</li>
                <li>Accept responsibility for all activities under your account</li>
              </ul>

              <h3>6. Prohibited Activities (Per Article 6 of Cybersecurity Law)</h3>
              <p>You agree NOT to use the Service to:</p>
              <ul>
                <li>Organize or participate in acts against the State of the Socialist Republic of Vietnam</li>
                <li>Incite violence, discrimination, or hatred</li>
                <li>Spread false or misleading information that causes confusion or damages social order</li>
                <li>Infringe upon national security, national defense, social safety, or public order</li>
                <li>Infringe upon intellectual property rights, privacy, or other legal rights of others</li>
                <li>Hack, spread viruses, malware, or harmful code</li>
                <li>Use another person's account without authorization</li>
                <li>Post or share prohibited content as defined by Vietnamese law</li>
                <li>Obstruct or disrupt the Service operations</li>
              </ul>

              <h3>7. User-Generated Content</h3>
              <p>7.1. You retain ownership of content you post on emoozi.</p>
              <p>7.2. By posting content, you grant us a limited license to display and manage your content on our platform.</p>
              <p>7.3. You represent and warrant that you have all rights to the content you post.</p>
              <p>7.4. We reserve the right to remove content that violates these Terms or Vietnamese law.</p>

              <h3>8. Privacy and Data Protection</h3>
              <p>8.1. We are committed to protecting your privacy and personal data in accordance with Vietnam's Law on Information Security.</p>
              <p>8.2. We collect and process personal data only as necessary to provide our Service.</p>
              <p>8.3. Your data is stored securely and will not be sold to third parties.</p>
              <p>8.4. You have the right to access, correct, or delete your personal data.</p>
              <p>8.5. For detailed information, please review our Privacy Policy.</p>

              <h3>9. Platform Responsibilities</h3>
              <p>We commit to:</p>
              <ul>
                <li>Implement measures to protect cybersecurity in accordance with the law</li>
                <li>Remove prohibited content when detected or upon request from authorities</li>
                <li>Cooperate with Vietnamese authorities in preventing and handling cybersecurity incidents</li>
                <li>Store user data securely and protect against unauthorized access</li>
                <li>Provide mechanisms for users to report violations</li>
              </ul>

              <h3>10. Limitation of Liability</h3>
              <p>10.1. The Service is provided "as is" without any warranties.</p>
              <p>10.2. We do not guarantee that the Service will be uninterrupted or error-free.</p>
              <p>10.3. We shall not be liable for any indirect, incidental, or consequential damages arising from your use of the Service.</p>
              <p>10.4. You shall be liable for any damages caused by your violation of these Terms or Vietnamese law.</p>

              <h3>11. Termination</h3>
              <p>11.1. We reserve the right to suspend or terminate your account at any time for violations of these Terms or Vietnamese law.</p>
              <p>11.2. Upon termination, your right to use the Service will immediately cease.</p>
              <p>11.3. You may delete your account at any time through your account settings.</p>

              <h3>12. Changes to Terms</h3>
              <p>We may update these Terms from time to time to comply with changes in Vietnamese law or to improve our Service. Continued use of the Service after changes constitutes acceptance of the new Terms.</p>

              <h3>13. Governing Law and Dispute Resolution</h3>
              <p>13.1. These Terms shall be governed by and construed in accordance with the laws of Vietnam.</p>
              <p>13.2. Any disputes arising from these Terms shall be resolved in accordance with Vietnamese law.</p>

              <h3>14. Contact Information</h3>
              <p>If you have questions about these Terms, please contact us at: qa@emoozi.com</p>

              <hr />

              {/* ===== ĐIỀU KHOẢN SỬ DỤNG - VIETNAMESE ===== */}
              <h2>Điều Khoản Sử Dụng</h2>
              <p><em>Ngày có hiệu lực: 4 tháng 3 năm 2026</em></p>

              <h3>1. Chấp Nhận Điều Khoản</h3>
              <p>Bằng việc truy cập và sử dụng emoozi ("Dịch vụ"), bạn đồng ý bị ràng buộc bởi các Điều Khoản Sử Dụng này. Nếu bạn không đồng ý với các điều khoản, vui lòng không sử dụng Dịch vụ của chúng tôi.</p>

              <h3>2. Mô Tả Dịch Vụ</h3>
              <p>emoozi là nền tảng mạng xã hội riêng tư cho phép người dùng chia sẻ khoảnh khắc, nhắn tin với gia đình và bạn bè, cũng như giữ liên lạc trong môi trường không có thuật toán.</p>

              <h3>3. Cơ Sở Pháp Lý</h3>
              <p>Điều Khoản Sử Dụng này được xây dựng tuân thủ:</p>
              <ul>
                <li>Luật An Ninh Mạng số 24/2018/QH14</li>
                <li>Bộ Luật Dân sự Việt Nam</li>
                <li>Luật An Toàn Thông Tin Mạng</li>
                <li>Luật Bảo Vệ Quyền Lợi Người Tiêu Dùng</li>
              </ul>

              <h3>4. Đăng Ký Tài Khoản Người Dùng</h3>
              <p>4.1. Bạn phải cung cấp thông tin đăng ký chính xác, đầy đủ và cập nhật.</p>
              <p>4.2. Bạn chịu trách nhiệm bảo mật thông tin đăng nhập của mình.</p>
              <p>4.3. Bạn phải từ 13 tuổi trở lên để sử dụng Dịch vụ này.</p>
              <p>4.4. Bạn chịu trách nhiệm cho mọi hoạt động dưới tài khoản của mình.</p>
              <p>4.5. Bạn phải cung cấp giấy tờ hợp lệ khi được yêu cầu theo quy định pháp luật.</p>

              <h3>5. Nghĩa Vụ Người Dùng (Theo Điều 5 Luật An Ninh Mạng)</h3>
              <p>Bạn đồng ý:</p>
              <ul>
                <li>Tuân thủ pháp luật Việt Nam và Điều Khoản Sử Dụng này</li>
                <li>Cung cấp thông tin cá nhân chính xác và đầy đủ</li>
                <li>Không sử dụng Dịch vụ cho mục đích bất hợp pháp</li>
                <li>Không xâm phạm quyền và lợi ích hợp pháp của người khác</li>
                <li>Cập nhật thông tin khi có thay đổi</li>
                <li>Chịu trách nhiệm về mọi hoạt động dưới tài khoản của mình</li>
              </ul>

              <h3>6. Các Hành Vi Bị Cấm (Theo Điều 6 Luật An Ninh Mạng)</h3>
              <p>Bạn đồng ý KHÔNG sử dụng Dịch vụ để:</p>
              <ul>
                <li>Tổ chức hoặc tham gia các hành vi chống Nhà nước Cộng hòa Xã hội chủ nghĩa Việt Nam</li>
                <li>Kích động bạo lực, phân biệt đối xử hoặc thù địch</li>
                <li>Truyền bá thông tin sai sự thật gây hoang mang hoặc phá hoại trật tự xã hội</li>
                <li>Xâm phạm an ninh quốc gia, quốc phòng, an toàn xã hội hoặc trật tự công cộng</li>
                <li>Xâm phạm quyền sở hữu trí tuệ, quyền riêng tư hoặc quyền pháp lý của người khác</li>
                <li>Hack, phát tán virus, phần mềm độc hại hoặc mã có hại</li>
                <li>Sử dụng tài khoản của người khác mà không được phép</li>
                <li>Đăng tải hoặc chia sẻ nội dung bị cấm theo pháp luật Việt Nam</li>
                <li>Cản trở hoặc gián đoạn hoạt động của Dịch vụ</li>
              </ul>

              <h3>7. Nội Dung Người Dùng</h3>
              <p>7.1. Bạn giữ quyền sở hữu nội dung bạn đăng trên emoozi.</p>
              <p>7.2. Bằng việc đăng nội dung, bạn cấp cho chúng tôi giấy phép hạn chế để hiển thị và quản lý nội dung của bạn trên nền tảng.</p>
              <p>7.3. Bạn tuyên bố và đảm bảo rằng bạn có tất cả quyền đối với nội dung bạn đăng.</p>
              <p>7.4. Chúng tôi có quyền xóa nội dung vi phạm Điều Khoản này hoặc pháp luật Việt Nam.</p>

              <h3>8. Quyền Riêng Tư và Bảo Vệ Dữ Liệu</h3>
              <p>8.1. Chúng tôi cam kết bảo vệ quyền riêng tư và dữ liệu cá nhân của bạn theo Luật An Toàn Thông Tin Mạng Việt Nam.</p>
              <p>8.2. Chúng tôi thu thập và xử lý dữ liệu cá nhân chỉ khi cần thiết để cung cấp Dịch vụ.</p>
              <p>8.3. Dữ liệu của bạn được lưu trữ an toàn và sẽ không được bán cho bên thứ ba.</p>
              <p>8.4. Bạn có quyền truy cập, chỉnh sửa hoặc xóa dữ liệu cá nhân của mình.</p>
              <p>8.5. Để biết thông tin chi tiết, vui lòng xem Chính Sách Quyền Riêng Tư.</p>

              <h3>9. Trách Nhiệm Nền Tảng</h3>
              <p>Chúng tôi cam kết:</p>
              <ul>
                <li>Thực hiện các biện pháp bảo vệ an ninh mạng theo quy định pháp luật</li>
                <li>Xóa nội dung bị cấm khi phát hiện hoặc theo yêu cầu của cơ quan chức năng</li>
                <li>Phối hợp với các cơ quan chức năng Việt Nam trong phòng ngừa và xử lý sự cố an ninh mạng</li>
                <li>Lưu trữ dữ liệu người dùng an toàn và bảo vệ chống truy cập trái phép</li>
                <li>Cung cấp cơ chế để người dùng báo cáo vi phạm</li>
              </ul>

              <h3>10. Giới Hạn Trách Nhiệm</h3>
              <p>10.1. Dịch vụ được cung cấp "nguyên trạng" không có bất kỳ bảo đảm nào.</p>
              <p>10.2. Chúng tôi không đảm bảo rằng Dịch vụ sẽ không bị gián đoạn hoặc không có lỗi.</p>
              <p>10.3. Chúng tôi không chịu trách nhiệm cho bất kỳ thiệt hại gián tiếp, ngẫu nhiên hoặc hậu quả nào phát sinh từ việc bạn sử dụng Dịch vụ.</p>
              <p>10.4. Bạn chịu trách nhiệm về mọi thiệt hại gây ra do vi phạm Điều Khoản này hoặc pháp luật Việt Nam.</p>

              <h3>11. Chấm Dứt</h3>
              <p>11.1. Chúng tôi có quyền đình chỉ hoặc chấm dứt tài khoản của bạn bất kỳ lúc nào khi vi phạm Điều Khoản này hoặc pháp luật Việt Nam.</p>
              <p>11.2. Khi chấm dứt, quyền sử dụng Dịch vụ của bạn sẽ chấm dứt ngay lập tức.</p>
              <p>11.3. Bạn có thể xóa tài khoản của mình bất kỳ lúc nào thông qua cài đặt tài khoản.</p>

              <h3>12. Thay Đổi Điều Khoản</h3>
              <p>Chúng tôi có thể cập nhật các Điều Khoản này theo thời gian để tuân thủ các thay đổi trong pháp luật Việt Nam hoặc cải thiện Dịch vụ của chúng tôi. Việc tiếp tục sử dụng Dịch vụ sau khi thay đổi đồng nghĩa với việc chấp nhận các Điều Khoản mới.</p>

              <h3>13. Luật Điều Chỉnh và Giải Quyết Tranh Chấp</h3>
              <p>13.1. Các Điều Khoản này được điều chỉnh và giải thích theo pháp luật Việt Nam.</p>
              <p>13.2. Mọi tranh chấp phát sinh từ các Điều Khoản này sẽ được giải quyết theo pháp luật Việt Nam.</p>

              <h3>14. Thông Tin Liên Hệ</h3>
              <p>Nếu bạn có câu hỏi về các Điều Khoản này, vui lòng liên hệ với chúng tôi tại: qa@emoozi.com</p>

              <hr />

              {/* ===== PRIVACY POLICY - ENGLISH ===== */}
              <h2>Privacy Policy</h2>
              <p><em>Effective Date: March 4, 2026</em></p>

              <h3>1. Introduction</h3>
              <p>emoozi ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our private social network platform.</p>

              <h3>2. Legal Basis</h3>
              <p>This Privacy Policy is prepared in compliance with:</p>
              <ul>
                <li>Vietnam Cybersecurity Law No. 24/2018/QH14</li>
                <li>Law on Information Security No. 86/2015/QH13</li>
                <li>Decree No. 13/2023/NĐ-CP on Personal Data Protection</li>
              </ul>

              <h3>3. Information We Collect</h3>
              <p><strong>3.1 Personal Information You Provide</strong></p>
              <ul>
                <li>Account information (username, email, phone, display name)</li>
                <li>Profile information (avatar, bio)</li>
                <li>Content you post (text, images, videos, voice messages)</li>
                <li>Messages you send to other users</li>
                <li>Friend connections and interactions</li>
              </ul>
              <p><strong>3.2 Automatically Collected Information</strong></p>
              <ul>
                <li>Device information (device type, operating system)</li>
                <li>Usage data (features used, time spent)</li>
                <li>IP address and access times</li>
              </ul>

              <h3>4. How We Use Your Information</h3>
              <p>We use your information to:</p>
              <ul>
                <li>Provide and maintain our Service</li>
                <li>Process your registration and account management</li>
                <li>Enable social features (messaging, sharing, friending)</li>
                <li>Improve and personalize your experience</li>
                <li>Communicate with you about updates and support</li>
                <li>Comply with legal obligations</li>
              </ul>

              <h3>5. Data Protection Principles</h3>
              <p>We adhere to the following principles:</p>
              <ul>
                <li><strong>Lawfulness, fairness, and transparency:</strong> We process data legally and transparently</li>
                <li><strong>Purpose limitation:</strong> We collect data for specified, explicit purposes only</li>
                <li><strong>Data minimization:</strong> We collect only necessary data</li>
                <li><strong>Accuracy:</strong> We keep data accurate and up-to-date</li>
                <li><strong>Storage limitation:</strong> We retain data only as long as necessary</li>
                <li><strong>Integrity and confidentiality:</strong> We protect data with appropriate security measures</li>
              </ul>

              <h3>6. Data Sharing and Disclosure</h3>
              <p>6.1. <strong>We do NOT sell your personal data</strong> to third parties.</p>
              <p>6.2. We may share data with:</p>
              <ul>
                <li>Service providers who assist in operating our platform</li>
                <li>Legal authorities when required by Vietnamese law</li>
                <li>Users you choose to interact with (within the platform)</li>
              </ul>
              <p>6.3. Any data sharing complies with Vietnamese law and is limited to what is necessary.</p>

              <h3>7. Data Storage and Security</h3>
              <p>7.1. Your data is stored on servers located in Vietnam.</p>
              <p>7.2. We implement appropriate technical and organizational measures:</p>
              <ul>
                <li>Encryption of data in transit and at rest</li>
                <li>Access controls and authentication</li>
                <li>Regular security assessments</li>
                <li>Employee confidentiality obligations</li>
              </ul>

              <h3>8. Your Rights</h3>
              <p>Under Vietnamese law, you have the right to:</p>
              <ul>
                <li>Access your personal data</li>
                <li>Correct inaccurate personal data</li>
                <li>Request deletion of your personal data</li>
                <li>Object to processing of your data</li>
                <li>Data portability</li>
                <li>Withdraw consent</li>
              </ul>
              <p>To exercise these rights, please contact us at: qa@emoozi.com</p>

              <h3>9. Data Retention</h3>
              <p>9.1. We retain your personal data for as long as your account is active.</p>
              <p>9.2. After account deletion:</p>
              <ul>
                <li>Data is deleted within 30 days</li>
                <li>Some data may be retained as required by law</li>
              </ul>

              <h3>10. Children's Privacy</h3>
              <p>10.1. Our Service is not intended for children under 13.</p>
              <p>10.2. We do not knowingly collect data from children under 13.</p>
              <p>10.3. If we discover such data, it will be promptly deleted.</p>

              <h3>11. Changes to This Policy</h3>
              <p>We may update this Privacy Policy to reflect changes in our practices or Vietnamese law. We will notify you of any material changes.</p>

              <h3>12. Contact Information</h3>
              <p>If you have questions about this Privacy Policy, please contact us at: qa@emoozi.com</p>

              <hr />

              {/* ===== CHÍNH SÁCH QUYỀN RIÊNG TƯ - VIETNAMESE ===== */}
              <h2>Chính Sách Quyền Riêng Tư</h2>
              <p><em>Ngày có hiệu lực: 4 tháng 3 năm 2026</em></p>

              <h3>1. Giới Thiệu</h3>
              <p>emoozi ("chúng tôi") cam kết bảo vệ quyền riêng tư của bạn. Chính Sách Quyền Riêng Tư này giải thích cách chúng tôi thu thập, sử dụng, tiết lộ và bảo vệ thông tin của bạn khi sử dụng nền tảng mạng xã hội riêng tư của chúng tôi.</p>

              <h3>2. Cơ Sở Pháp Lý</h3>
              <p>Chính Sách này được xây dựng tuân thủ:</p>
              <ul>
                <li>Luật An Ninh Mạng số 24/2018/QH14</li>
                <li>Luật An Toàn Thông Tin Mạng số 86/2015/QH13</li>
                <li>Nghị định số 13/2023/NĐ-CP về Bảo Vệ Dữ Liệu Cá Nhân</li>
              </ul>

              <h3>3. Thông Tin Chúng Tôi Thu Thập</h3>
              <p><strong>3.1 Thông Tin Bạn Cung Cấp</strong></p>
              <ul>
                <li>Thông tin tài khoản (tên đăng nhập, email, điện thoại, tên hiển thị)</li>
                <li>Thông tin hồ sơ (ảnh đại diện, tiểu sử)</li>
                <li>Nội dung bạn đăng (văn bản, hình ảnh, video, tin thoại)</li>
                <li>Tin nhắn bạn gửi cho người dùng khác</li>
                <li>Kết nối bạn bè và tương tác</li>
              </ul>
              <p><strong>3.2 Thông Tin Thu Thập Tự Động</strong></p>
              <ul>
                <li>Thông tin thiết bị (loại thiết bị, hệ điều hành)</li>
                <li>Dữ liệu sử dụng (tính năng sử dụng, thời gian truy cập)</li>
                <li>Địa chỉ IP và thời gian truy cập</li>
              </ul>

              <h3>4. Cách Chúng Tôi Sử Dụng Thông Tin</h3>
              <p>Chúng tôi sử dụng thông tin của bạn để:</p>
              <ul>
                <li>Cung cấp và duy trì Dịch vụ</li>
                <li>Xử lý đăng ký và quản lý tài khoản</li>
                <li>Bật tính năng xã hội (nhắn tin, chia sẻ, kết bạn)</li>
                <li>Cải thiện và cá nhân hóa trải nghiệm của bạn</li>
                <li>Liên lạc với bạn về cập nhật và hỗ trợ</li>
                <li>Tuân thủ nghĩa vụ pháp luật</li>
              </ul>

              <h3>5. Nguyên Tắc Bảo Vệ Dữ Liệu</h3>
              <p>Chúng tôi tuân thủ các nguyên tắc sau:</p>
              <ul>
                <li><strong>Hợp pháp, công bằng và minh bạch:</strong> Xử lý dữ liệu hợp pháp và minh bạch</li>
                <li><strong>Giới hạn mục đích:</strong> Thu thập dữ liệu cho mục đích cụ thể, rõ ràng</li>
                <li><strong>Tối thiểu hóa dữ liệu:</strong> Chỉ thu thập dữ liệu cần thiết</li>
                <li><strong>Độ chính xác:</strong> Giữ dữ liệu chính xác và cập nhật</li>
                <li><strong>Giới hạn lưu trữ:</strong> Chỉ lưu dữ liệu khi cần thiết</li>
                <li><strong>Tính toàn vẹn và bảo mật:</strong> Bảo vệ dữ liệu bằng biện pháp bảo mật phù hợp</li>
              </ul>

              <h3>6. Chia Sẻ và Tiết Lộ Dữ Liệu</h3>
              <p>6.1. <strong>Chúng tôi KHÔNG bán dữ liệu cá nhân của bạn</strong> cho bên thứ ba.</p>
              <p>6.2. Chúng tôi có thể chia sẻ dữ liệu với:</p>
              <ul>
                <li>Nhà cung cấp dịch vụ hỗ trợ vận hành nền tảng</li>
                <li>Cơ quan pháp luật khi được pháp luật Việt Nam yêu cầu</li>
                <li>Người dùng bạn chọn tương tác (trong nền tảng)</li>
              </ul>
              <p>6.3. Mọi chia sẻ dữ liệu tuân thủ pháp luật Việt Nam và giới hạn ở mức cần thiết.</p>

              <h3>7. Lưu Trữ và Bảo Mật Dữ Liệu</h3>
              <p>7.1. Dữ liệu của bạn được lưu trữ trên máy chủ tại Việt Nam.</p>
              <p>7.2. Chúng tôi thực hiện các biện pháp kỹ thuật và tổ chức phù hợp:</p>
              <ul>
                <li>Mã hóa dữ liệu khi truyền và lưu trữ</li>
                <li>Kiểm soát truy cập và xác thực</li>
                <li>Đánh giá bảo mật định kỳ</li>
                <li>Nghĩa vụ bảo mật của nhân viên</li>
              </ul>

              <h3>8. Quyền Của Bạn</h3>
              <p>Theo pháp luật Việt Nam, bạn có quyền:</p>
              <ul>
                <li>Truy cập dữ liệu cá nhân của bạn</li>
                <li>Sửa dữ liệu cá nhân không chính xác</li>
                <li>Yêu cầu xóa dữ liệu cá nhân</li>
                <li>Phản đối xử lý dữ liệu của bạn</li>
                <li>Chuyển giao dữ liệu</li>
                <li>Rút lại sự đồng ý</li>
              </ul>
              <p>Để thực hiện các quyền này, vui lòng liên hệ: qa@emoozi.com</p>

              <h3>9. Lưu Giữ Dữ Liệu</h3>
              <p>9.1. Chúng tôi lưu giữ dữ liệu cá nhân của bạn khi tài khoản còn hoạt động.</p>
              <p>9.2. Sau khi xóa tài khoản:</p>
              <ul>
                <li>Dữ liệu được xóa trong vòng 30 ngày</li>
                <li>Một số dữ liệu có thể được lưu giữ theo yêu cầu của pháp luật</li>
              </ul>

              <h3>10. Quyền Riêng Tư Trẻ Em</h3>
              <p>10.1. Dịch vụ của chúng tôi không dành cho trẻ em dưới 13 tuổi.</p>
              <p>10.2. Chúng tôi không cố tình thu thập dữ liệu từ trẻ em dưới 13 tuổi.</p>
              <p>10.3. Nếu phát hiện dữ liệu như vậy, chúng tôi sẽ xóa ngay lập tức.</p>

              <h3>11. Thay Đổi Chính Sách</h3>
              <p>Chúng tôi có thể cập nhật Chính Sách Quyền Riêng Tư này để phản ánh thay đổi trong hoạt động hoặc pháp luật Việt Nam. Chúng tôi sẽ thông báo cho bạn về bất kỳ thay đổi quan trọng nào.</p>

              <h3>12. Thông Tin Liên Hệ</h3>
              <p>Nếu bạn có câu hỏi về Chính Sách Quyền Riêng Tư này, vui lòng liên hệ: qa@emoozi.com</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Register;
